import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import { GridCellEditCommitParams } from '@/global/components/DataGrid/utils/types'
import IconeAjuda from '@/global/components/IconeAjuda'
import ContentGuide from '@/global/guides/ContentGuide'
import Dialog from '@/global/guides/Dialog'
import { hasPermission } from '@/hooks/useBackendMenu'
import { useRubricasOutletContext } from '@/pages/Rubricas'
import {
    empresas as empresas_api,
    rubrica as rubricas_api,
} from '@/service/api'
import { invariant } from '@acme/core'
import { IconButton } from '@mui/material'
import { LinkIcon, Trash2Icon } from 'lucide-react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

export default function ExcecoesRubricas() {
    const { data: empresas = [], isLoading } = empresas_api.useFetch()
    const { data } = useRubricasOutletContext()
    const { id } = useParams()

    const { mutate: upsert } = rubricas_api.excecao.mutations.useCreate({
        onError: e => {
            toast.error(e.message)
        },
        invalidateQuery: true,
    })
    const { mutate: deleteExcecao } = rubricas_api.excecao.mutations.useDelete({
        onSuccess: async () => {
            await rubricas_api.invalidateQueries()
            toast.success('Exceção excluída com sucesso')
        },
        onError: (e: any) => {
            toast.error(e.message)
        },
        invalidateQuery: true,
    })

    if (!id) return null
    const rubrica = data.find(r => r.id === Number(id))
    if (!rubrica) return null

    const rows = empresas
        .map(empresa => {
            const excecao = rubrica.ExcecaoRubrica?.find(
                    it => it.empresaId === empresa.id,
                ),
                id = excecao ? `${rubrica.id}-${empresa.id}` : undefined,
                codigo = excecao ? excecao.codigo : rubrica.codigo

            return {
                id,
                rubricaId: rubrica.id,
                empresaId: empresa.id,
                codigo,
                nomeFantasia: empresa.nomeFantasia,
            }
        })
        .sort((a, b) => a.nomeFantasia.localeCompare(b.nomeFantasia))

    function handleCellEditCommit({
        field,
        value,
        row,
    }: GridCellEditCommitParams) {
        invariant(!!rubrica, 'Rubrica não encontrada')

        if (!value) return toast.warning('O valor não foi salvo')

        if (field === 'codigo') {
            if (Number(value) === rubrica.codigo)
                return deleteExcecao(row.id as any)

            return upsert(
                {
                    empresaId: row.empresaId,
                    rubricaId: rubrica.id,
                    codigo: Number(value),
                },
                {
                    onSuccess: async () => {
                        await rubricas_api.invalidateQueries()
                        toast.success('Código atualizado com sucesso')
                    },
                },
            )
        }
    }

    return (
        <Dialog.Root>
            <Dialog.Title>Gerenciar exceções</Dialog.Title>
            <Dialog.Content dividers>
                <ContentGuide.DataGrid
                    loading={isLoading}
                    onCellEditCommit={handleCellEditCommit}
                    columns={[
                        {
                            field: 'codigo',
                            headerName: 'Código',
                            width: 150,
                            editable: hasPermission(['update', 'Rubricas']),
                            type: 'number',
                        },
                        {
                            field: 'nomeFantasia',
                            headerName: 'Empresa',
                            flex: 1,
                        },
                        {
                            ...optionsColumn,
                            headerAlign: 'center',
                            field: '__excluir',
                            // TODO: user IconButton do global depois do merge
                            renderCell: ({ row: { id } }) =>
                                id ? (
                                    <IconButton
                                        title="Excluir exceção e utilizar código padrão"
                                        onClick={() => deleteExcecao(id as any)}
                                    >
                                        <Trash2Icon className="text-red-500" />
                                    </IconButton>
                                ) : (
                                    <IconButton
                                        disabled
                                        title="Utiliza o código padrão"
                                    >
                                        <LinkIcon />
                                    </IconButton>
                                ),
                            renderHeader: () => (
                                <IconeAjuda>
                                    Clique sobre o campo código para editar ou
                                    criar uma exceção.
                                    <br />
                                    <br />O ícone{' '}
                                    <LinkIcon className="inline size-4" />{' '}
                                    indica que a empresa está utilizando o
                                    código padrão da rubrica.
                                    <br /> Já o ícone{' '}
                                    <Trash2Icon className="inline text-red-500 size-4" />{' '}
                                    indica que a empresa possui uma exceção e a
                                    mesma pode ser excluída.
                                </IconeAjuda>
                            ),
                        },
                    ]}
                    rows={rows}
                    pageSize={Infinity}
                    hideFooter
                    ToolbarProps={{
                        showQuickFilter: true,
                        hideFilterButton: true,
                    }}
                />
            </Dialog.Content>
            <Dialog.Actions>
                <Dialog.Actions.SecondaryButton />
            </Dialog.Actions>
        </Dialog.Root>
    )
}
